// ParentDashboard.jsx — Interface Parent : dossier de mes enfants + assiduité + événements.
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Users, MapPin, Clock, CalendarDays, ArrowLeft, UserRound, TrendingUp, Ticket } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { RoleShell } from './RoleShell';

const STATUT_LABEL = {
  present: { label: 'Présent', color: '#10B981' },
  absent: { label: 'Absent', color: '#EF4444' },
  excuse: { label: 'Excusé', color: '#F59E0B' },
  inconnu: { label: 'Non renseigné', color: '#94A3B8' },
};

export default function ParentDashboard() {
  const { user } = useAuth();
  const accent = user?.couleur_principale || '#10B981';
  const [enfants, setEnfants] = useState(null);
  const [evenements, setEvenements] = useState([]);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.get('/parent/enfants').then(({ data }) => setEnfants(data)).catch(() => setEnfants([]));
    api.get('/parent/evenements').then(({ data }) => setEvenements(data)).catch(() => {});
  }, []);

  if (detail) return <EnfantPresences enfantId={detail} accent={accent} onBack={() => setDetail(null)} />;

  return (
    <RoleShell roleLabel="Parent" title="Espace famille" subtitle="Suivez la vie sportive de vos enfants." testid="parent-dashboard">
      {enfants === null ? (
        <div className="grid gap-4 sm:grid-cols-2">{[0, 1].map((i) => <div key={i} className="h-52 rounded-2xl bg-white border border-[#F1F5F9] animate-pulse" />)}</div>
      ) : enfants.length === 0 ? (
        <EmptyState icon={<UserRound className="w-8 h-8" />} title="Aucun enfant rattaché" text="Aucun enfant n'est encore associé à votre compte." />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2" data-testid="parent-enfants-list">
          {enfants.map((c) => {
            const groupe = c.groupes?.[0];
            return (
              <div key={c.id} className="bg-white border-2 border-[#F1F5F9] rounded-2xl p-5" data-testid={`enfant-card-${c.id}`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-full grid place-items-center text-white font-display font-black text-lg" style={{ background: accent }}>{(c.prenom || '?')[0]}</div>
                  <div>
                    <h3 className="font-display font-black text-lg text-[#0F172A]">{c.prenom} {c.nom}</h3>
                    {groupe && <p className="text-xs font-bold uppercase tracking-wider" style={{ color: accent }}>{groupe.nom}{groupe.categorie ? ` · ${groupe.categorie}` : ''}</p>}
                  </div>
                </div>

                <div className="space-y-1.5 text-sm text-[#475569] mb-3">
                  {groupe && (groupe.jour || groupe.heure) && <div className="flex items-center gap-2"><Clock className="w-4 h-4" style={{ color: accent }} /> {groupe.jour} {groupe.heure}</div>}
                  {groupe && groupe.lieu && <div className="flex items-center gap-2"><MapPin className="w-4 h-4" style={{ color: accent }} /> {groupe.lieu}</div>}
                  {c.coaches?.length > 0 && <div className="flex items-center gap-2"><Users className="w-4 h-4" style={{ color: accent }} /> Encadrant : {c.coaches.map((x) => x.prenom + ' ' + x.nom).join(', ')}</div>}
                </div>

                <div className="flex items-center justify-between bg-[#F9F9F8] rounded-xl px-4 py-3 mb-3">
                  <div className="flex items-center gap-2 text-sm font-semibold text-[#0F172A]"><TrendingUp className="w-4 h-4" style={{ color: accent }} /> Assiduité</div>
                  <div className="text-right">
                    <span className="font-display font-black text-lg" style={{ color: accent }}>{c.assiduite_pct === null ? '—' : `${c.assiduite_pct}%`}</span>
                    <span className="text-xs text-[#94A3B8] ml-1">({c.presence_present}/{c.presence_total})</span>
                  </div>
                </div>

                <button onClick={() => setDetail(c.id)} className="w-full text-sm font-bold rounded-xl py-2.5 border-2 transition-all hover:text-white" style={{ borderColor: accent, color: accent }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = accent; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}
                  data-testid={`enfant-presences-btn-${c.id}`}>
                  Voir l'assiduité détaillée
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Prochains événements */}
      <div className="mt-8">
        <h2 className="font-display font-black text-xl text-[#0F172A] mb-3 flex items-center gap-2"><CalendarDays className="w-5 h-5" style={{ color: accent }} /> Prochains événements du club</h2>
        {evenements.length === 0 ? (
          <p className="text-sm text-[#94A3B8]">Aucun événement à venir pour le moment.</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="parent-evenements">
            {evenements.map((e) => (
              <div key={e.id} className="bg-white border-2 border-[#F1F5F9] rounded-xl p-4">
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider mb-1" style={{ color: accent }}><Ticket className="w-3.5 h-3.5" /> Événement</div>
                <h3 className="font-display font-black text-[#0F172A]">{e.nom}</h3>
                {e.date_debut && <p className="text-xs text-[#475569] mt-1">{new Date(e.date_debut).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}</p>}
                {e.lieu && <p className="text-xs text-[#94A3B8]">{e.lieu}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </RoleShell>
  );
}

function EnfantPresences({ enfantId, accent, onBack }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/parent/enfant/${enfantId}/presences`).then(({ data }) => setData(data)).catch(() => toast.error('Chargement impossible'));
  }, [enfantId]);

  return (
    <RoleShell roleLabel="Parent" title={data ? `Assiduité — ${data.enfant.prenom}` : 'Chargement…'} subtitle="Historique des présences aux séances" testid="parent-presences">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm font-bold text-[#475569] hover:text-[#0F172A] mb-5" data-testid="presences-back-btn">
        <ArrowLeft className="w-4 h-4" /> Retour au dossier
      </button>
      {!data ? (
        <div className="h-48 rounded-2xl bg-white border border-[#F1F5F9] animate-pulse" />
      ) : data.presences.length === 0 ? (
        <EmptyState icon={<CalendarDays className="w-8 h-8" />} title="Aucune séance" text="Aucune présence enregistrée pour l'instant." />
      ) : (
        <div className="bg-white border-2 border-[#F1F5F9] rounded-2xl divide-y divide-[#F1F5F9]" data-testid="presences-list">
          {data.presences.map((p, i) => {
            const s = STATUT_LABEL[p.statut] || STATUT_LABEL.inconnu;
            return (
              <div key={i} className="flex items-center justify-between px-5 py-3">
                <span className="font-semibold text-[#0F172A]">{p.date}</span>
                <span className="text-xs font-bold px-3 py-1 rounded-full" style={{ background: `${s.color}1a`, color: s.color }}>{s.label}</span>
              </div>
            );
          })}
        </div>
      )}
    </RoleShell>
  );
}

function EmptyState({ icon, title, text }) {
  return (
    <div className="bg-white border-2 border-dashed border-[#E2E8F0] rounded-2xl p-10 text-center">
      <div className="w-14 h-14 rounded-2xl bg-[#F1F5F9] grid place-items-center mx-auto mb-3 text-[#94A3B8]">{icon}</div>
      <h3 className="font-display font-black text-[#0F172A]">{title}</h3>
      <p className="text-sm text-[#475569] mt-1">{text}</p>
    </div>
  );
}
